package dev.kotlinfest._2026.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import dev.kotlinfest._2026.components.SessionNotificationWatcher
import dev.kotlinfest._2026.components.sections.Footer
import dev.kotlinfest._2026.components.sections.NavHeader
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.w3c.dom.Document
import org.w3c.dom.HTMLMetaElement

val PageContentStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .padding(top = 3.cssRem, bottom = 3.cssRem, leftRight = 1.25.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .maxWidth(62.cssRem)
            .padding(top = 4.cssRem, bottom = 4.cssRem, leftRight = 2.5.cssRem)
    }
}

val NavHeaderSpacerStyle = CssStyle {
    base {
        Modifier.fillMaxWidth().height(80.px)
    }
    Breakpoint.MD {
        Modifier.height(160.px)
    }
}

private const val SiteName = "Kotlin Fest 2026"
private const val SiteUrl = "https://2026.kotlinfest.dev"
const val SiteDescription =
    "Kotlin Festは「Kotlinを愛でる」をビジョンに、Kotlin™に関する知見の共有と、Kotlinファンの交流の場を提供する技術カンファレンスです。"

class PageLayoutData(
    val title: String,
    val description: String = SiteDescription,
    val fullWidth: Boolean = false,
)

private fun Document.setMetaContent(selector: String, attribute: String, value: String) {
    val head = head ?: return
    val meta = (head.querySelector(selector) as? HTMLMetaElement)
        ?: (createElement("meta") as HTMLMetaElement).also {
            it.setAttribute(attribute, selector.substringAfter("='").substringBefore("']"))
            head.appendChild(it)
        }
    meta.content = value
}

@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    LaunchedEffect(data.title, data.description) {
        val pageTitle = if (data.title == "Home") SiteName else "$SiteName - ${data.title}"
        document.title = pageTitle
        document.setMetaContent("meta[name='description']", "name", data.description)
        document.setMetaContent("meta[property='og:title']", "property", pageTitle)
        document.setMetaContent("meta[property='og:description']", "property", data.description)
        document.setMetaContent("meta[property='og:url']", "property", "$SiteUrl${window.location.pathname}")
    }
    SessionNotificationWatcher()

    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            // Create a box with two rows: the main content (fills as much space as it can) and the footer (which reserves
            // space at the bottom). "min-content" means the use the height of the row, which we use for the footer.
            // Since this box is set to *at least* 100%, the footer will always appear at least on the bottom but can be
            // pushed further down if the first row grows beyond the page.
            // Grids are powerful but have a bit of a learning curve. For more info, see:
            // https://css-tricks.com/snippets/css/complete-guide-grid/
            .gridTemplateRows {
                size(1.fr)
                size(minContent)
            },
        contentAlignment = Alignment.Center,
    ) {
        Column(
            // Isolate the content, because otherwise the absolute-positioned SVG above will render on top of it.
            // This is confusing but how browsers work. Read up on stacking contexts for more info.
            // https://developer.mozilla.org/en-US/docs/Web/CSS/CSS_positioned_layout/Understanding_z-index/Stacking_context
            // Some people might have used z-index instead, but best practice is to avoid that if possible, because
            // as a site gets complex, Z-fighting can be a huge pain to track down.
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            if (data.fullWidth) {
                Box(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth()) {
                        content()
                    }
                    NavHeader()
                }
            } else {
                NavHeader()
                Box(NavHeaderSpacerStyle.toModifier())
                Column(PageContentStyle.toModifier()) {
                    content()
                }
            }
        }
        // Associate the footer with the row that will get pushed off the bottom of the page if it can't fit.
        Footer(Modifier.fillMaxWidth().gridRow(2))
    }
}
